import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Boxes, DatabaseZap, Play, ScanSearch } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard, formatNumber } from '../../../components/design-system/admin';
import { ProductImageTile } from '../../../components/design-system/common/ProductImageTile';
import { SizeLabel } from '../../../components/design-system/common/SizeLabel';
import { networkErrorMessage, type NetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { BackfillResult, DiscoverResult, EnsureResult, MatchVendorResult, RetailerSummary, VisualState } from '../../../common/hooks/network-admin.types';
import { ErrorNote, VisualOffNote, methodLabel } from './network-parts';

const ALL = '';

function RetailerSelect({ id, value, onChange, retailers, disabled }: { id: string; value: string; onChange: (v: string) => void; retailers: RetailerSummary[]; disabled?: boolean }) {
    const { t } = useTranslation();
    return (
        <NativeSelect id={id} className="w-full sm:w-64" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label={t('ADMIN_NETWORK.FIELDS.RETAILER')}>
            <NativeSelectOption value={ALL}>{t('ADMIN_NETWORK.ACTIONS.ALL_RETAILERS')}</NativeSelectOption>
            {retailers.map((r) => (
                <NativeSelectOption key={r.vendorId} value={r.vendorId}>
                    {t('ADMIN_NETWORK.ACTIONS.RETAILER_OPTION', { name: r.name, linked: r.linkedProducts, products: r.products })}
                </NativeSelectOption>
            ))}
        </NativeSelect>
    );
}

function RunButton({ busy, onClick, icon, label, busyLabel, testId, disabled }: { busy: boolean; onClick: () => void; icon: ReactNode; label: string; busyLabel: string; testId: string; disabled?: boolean }) {
    return (
        <Button variant="outline" onClick={onClick} disabled={busy || disabled} data-testid={testId}>
            {busy ? <Spinner /> : icon}
            {busy ? busyLabel : label}
        </Button>
    );
}

/** One "visual off" note per distinct reason across a run's vendors. */
function VisualNotes({ states }: { states: (VisualState | undefined)[] }) {
    const off = states.filter((v): v is VisualState => !!v && !v.on);
    const reasons = Array.from(new Map(off.map((v) => [v.reason || '', v])).values());
    return (
        <>
            {reasons.map((v) => (
                <VisualOffNote key={v.reason || 'none'} visual={v} />
            ))}
        </>
    );
}

const num = (n: number | undefined) => <span className="tabular-nums">{formatNumber(n ?? 0)}</span>;

/** "Run matching": the matcher over every retailer, or one, with what each run did. */
function MatchingAction({ api, retailers, onChanged }: { api: NetworkAdminApi; retailers: RetailerSummary[]; onChanged: () => void }) {
    const { t } = useTranslation();
    const [vendorId, setVendorId] = useState(ALL);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<MatchVendorResult[] | null>(null);

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await api.runMatching(vendorId || undefined);
            setResults(res);
            onChanged();
        } catch (e) {
            setResults(null);
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SectionCard title={t('ADMIN_NETWORK.ACTIONS.MATCH_TITLE')} description={t('ADMIN_NETWORK.ACTIONS.MATCH_HINT')} testId="action-match">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <RetailerSelect id="match-retailer" value={vendorId} onChange={setVendorId} retailers={retailers} disabled={busy} />
                    <RunButton busy={busy} onClick={run} icon={<Play />} label={t('ADMIN_NETWORK.ACTIONS.MATCH_RUN')} busyLabel={t('ADMIN_NETWORK.ACTIONS.MATCH_RUNNING')} testId="match-run" />
                </div>
                {error && <ErrorNote message={error} onRetry={run} />}
                {results && (
                    <div className="flex flex-col gap-2" data-testid="match-results">
                        <VisualNotes states={results.map((r) => r.visual)} />
                        {!results.length ? (
                            <p className="text-sm text-muted-foreground">{t('ADMIN_NETWORK.ACTIONS.MATCH_NONE')}</p>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="ps-3">{t('ADMIN_NETWORK.FIELDS.RETAILER')}</TableHead>
                                            <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.EXAMINED')}</TableHead>
                                            <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.SUGGESTED')}</TableHead>
                                            <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.AUTO_CONFIRMED')}</TableHead>
                                            <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.ALREADY_LINKED')}</TableHead>
                                            <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.UNMATCHED')}</TableHead>
                                            <TableHead>{t('ADMIN_NETWORK.ACTIONS.BY_METHOD')}</TableHead>
                                            <TableHead className="pe-3 text-end">{t('ADMIN_NETWORK.ACTIONS.DURATION')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {results.map((r) => (
                                            <TableRow key={r.vendorId}>
                                                <TableCell className="ps-3 font-medium">{r.vendorName}</TableCell>
                                                <TableCell className="text-end">{num(r.products)}</TableCell>
                                                <TableCell className="text-end">{num(r.suggested)}</TableCell>
                                                <TableCell className="text-end">{num(r.confirmed)}</TableCell>
                                                <TableCell className="text-end">{num(r.skipped)}</TableCell>
                                                <TableCell className="text-end">{num(r.unmatched)}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">
                                                    {Object.entries(r.byMethod || {}).filter(([, n]) => n > 0).map(([m, n]) => `${methodLabel(t, m)} ${formatNumber(n)}`).join(' · ') || '—'}
                                                </TableCell>
                                                <TableCell className="pe-3 text-end text-xs text-muted-foreground tabular-nums">
                                                    {t('ADMIN_NETWORK.ACTIONS.SECONDS', { value: formatNumber((r.durationMs || 0) / 1000, 1) })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                        {results.some((r) => r.suggested > 0) && (
                            <Button variant="outline" size="sm" className="self-start" asChild>
                                <Link to="/network/review">{t('ADMIN_NETWORK.ACTIONS.GO_REVIEW')}</Link>
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

/** "Discover": products several shops sell that the network does not have — a dry run first, then drafts. */
function DiscoverAction({ api, onChanged }: { api: NetworkAdminApi; onChanged: () => void }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState<'dry' | 'create' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<DiscoverResult | null>(null);
    const [created, setCreated] = useState<DiscoverResult | null>(null);

    const run = async (dryRun: boolean) => {
        setBusy(dryRun ? 'dry' : 'create');
        setError(null);
        try {
            const res = await api.discover(dryRun);
            if (dryRun) {
                setPreview(res);
                setCreated(null);
            } else {
                setCreated(res);
                setPreview(null);
                onChanged();
            }
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(null);
        }
    };

    const shown = created ?? preview;
    const creatable = preview?.clusters.filter((c) => !!c.brand).length ?? 0;

    return (
        <SectionCard title={t('ADMIN_NETWORK.ACTIONS.DISCOVER_TITLE')} description={t('ADMIN_NETWORK.ACTIONS.DISCOVER_HINT')} testId="action-discover">
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <RunButton busy={busy === 'dry'} disabled={!!busy} onClick={() => run(true)} icon={<ScanSearch />} label={t('ADMIN_NETWORK.ACTIONS.DISCOVER_DRY')} busyLabel={t('ADMIN_NETWORK.ACTIONS.DISCOVER_LOOKING')} testId="discover-dry" />
                    {preview && (
                        <RunButton
                            busy={busy === 'create'}
                            disabled={!!busy || creatable === 0}
                            onClick={() => run(false)}
                            icon={<Boxes />}
                            label={t('ADMIN_NETWORK.ACTIONS.DISCOVER_CREATE', { count: creatable })}
                            busyLabel={t('ADMIN_NETWORK.ACTIONS.DISCOVER_CREATING')}
                            testId="discover-create"
                        />
                    )}
                </div>
                {error && <ErrorNote message={error} />}
                {shown && (
                    <div className="flex flex-col gap-2" data-testid={created ? 'discover-created' : 'discover-preview'}>
                        <VisualOffNote visual={shown.visual} />
                        <p className="text-sm">
                            {created
                                ? t('ADMIN_NETWORK.ACTIONS.DISCOVER_CREATED', { created: created.created, clusters: created.clusters.length, examined: created.examined })
                                : t('ADMIN_NETWORK.ACTIONS.DISCOVER_FOUND', { count: shown.clusters.length, examined: shown.examined })}
                        </p>
                        {!!shown.clusters.length && (
                            <ul className="flex flex-col divide-y rounded-lg border">
                                {shown.clusters.map((c, i) => (
                                    <li key={`${c.name}-${i}`} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium">
                                                {c.networkProductId ? <Link className="hover:underline" to={`/network/products/${c.networkProductId}`}>{c.name}</Link> : c.name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {c.brand
                                                    ? c.brand.created || !c.brand.id
                                                        ? t('ADMIN_NETWORK.ACTIONS.CLUSTER_NEW_BRAND', { brand: c.brand.name })
                                                        : c.brand.name
                                                    : t('ADMIN_NETWORK.ACTIONS.CLUSTER_NO_BRAND')}
                                                {' · '}
                                                <SizeLabel format={c.format} />
                                                {' · '}
                                                {t('ADMIN_NETWORK.ACTIONS.CLUSTER_MEMBERS', { count: c.members.length })}
                                            </p>
                                            {c.reason && <p className="mt-0.5 text-xs text-muted-foreground">{c.reason}</p>}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            {c.members.slice(0, 5).map((m) => (
                                                <ProductImageTile
                                                    key={`${m.vendorId}-${m.productUuid}`}
                                                    src={m.imageUrl}
                                                    alt={`${m.vendorName} — ${m.name}`}
                                                    size={40}
                                                    emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')}
                                                    openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')}
                                                />
                                            ))}
                                            {created && (
                                                <span className={cn('ms-2 text-xs', c.created ? 'text-foreground' : 'text-muted-foreground')}>
                                                    {c.created ? t('ADMIN_NETWORK.ACTIONS.CLUSTER_CREATED') : t('ADMIN_NETWORK.ACTIONS.CLUSTER_SKIPPED')}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

function EnsureLine({ label, r }: { label: string; r: EnsureResult }) {
    const { t } = useTranslation();
    return (
        <TableRow>
            <TableCell className="ps-3 font-medium">{label}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{r.model || '—'}</TableCell>
            <TableCell className="text-end">{num(r.embedded)}</TableCell>
            <TableCell className="text-end">{num(r.skipped)}</TableCell>
            <TableCell className={cn('text-end', r.failed > 0 && 'text-destructive')}>{num(r.failed)}</TableCell>
            <TableCell className="pe-3 text-xs text-muted-foreground">{r.unavailable ? t('ADMIN_NETWORK.ACTIONS.UNAVAILABLE', { reason: r.unavailable }) : ''}</TableCell>
        </TableRow>
    );
}

/** "Backfill embeddings": SurfaceID vectors for every product that has none under the current model. */
function BackfillAction({ api, retailers, onChanged }: { api: NetworkAdminApi; retailers: RetailerSummary[]; onChanged: () => void }) {
    const { t } = useTranslation();
    const [vendorId, setVendorId] = useState(ALL);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<BackfillResult | null>(null);

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await api.backfillEmbeddings(vendorId || undefined);
            setResult(res);
            onChanged();
        } catch (e) {
            setResult(null);
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SectionCard title={t('ADMIN_NETWORK.ACTIONS.BACKFILL_TITLE')} description={t('ADMIN_NETWORK.ACTIONS.BACKFILL_HINT')} testId="action-backfill">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <RetailerSelect id="backfill-retailer" value={vendorId} onChange={setVendorId} retailers={retailers} disabled={busy} />
                    <RunButton busy={busy} onClick={run} icon={<DatabaseZap />} label={t('ADMIN_NETWORK.ACTIONS.BACKFILL_RUN')} busyLabel={t('ADMIN_NETWORK.ACTIONS.BACKFILL_RUNNING')} testId="backfill-run" />
                </div>
                {error && <ErrorNote message={error} onRetry={run} />}
                {result && (
                    <div className="flex flex-col gap-2" data-testid="backfill-results">
                        {result.unavailable && <VisualOffNote visual={{ on: false, reason: result.unavailable }} />}
                        <div className="overflow-x-auto rounded-lg border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="ps-3">{t('ADMIN_NETWORK.ACTIONS.CATALOGUE')}</TableHead>
                                        <TableHead>{t('ADMIN_NETWORK.FIELDS.MODEL')}</TableHead>
                                        <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.EMBEDDED')}</TableHead>
                                        <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.SKIPPED')}</TableHead>
                                        <TableHead className="text-end">{t('ADMIN_NETWORK.ACTIONS.FAILED')}</TableHead>
                                        <TableHead className="pe-3"><span className="sr-only">{t('ADMIN_NETWORK.ACTIONS.NOTE')}</span></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {result.network && <EnsureLine label={t('ADMIN_NETWORK.ACTIONS.NETWORK_PRODUCTS')} r={result.network} />}
                                    {(result.vendors || []).map((v) => (
                                        <EnsureLine key={v.vendorId} label={v.name} r={v} />
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

/** The three operator actions of the overview, each showing its own result in place. */
export function NetworkActions({ api, retailers, onChanged }: { api: NetworkAdminApi; retailers: RetailerSummary[]; onChanged: () => void }) {
    return (
        <div className="flex flex-col gap-4">
            <MatchingAction api={api} retailers={retailers} onChanged={onChanged} />
            <DiscoverAction api={api} onChanged={onChanged} />
            <BackfillAction api={api} retailers={retailers} onChanged={onChanged} />
        </div>
    );
}
